/**
 * 新規登録ページ (Phase 3).
 *
 * email + password → POST /auth/register → メール確認待ち画面へ遷移
 * Figma: #13, #67 (バリデーション), #126 (ローディング)
 * UI: Login-style card layout (centered card on gray bg)
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { authRegister, googleOAuthAuthorizeUrl } from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';
import { getPendingInviteToken } from '../lib/invite-token.js';
import { trackEvent } from '../lib/posthog.js';

export function RegisterPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { oauth_error?: string };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = '新規登録 - PetaCMS';
    if (search?.oauth_error) {
      setError(
        search.oauth_error === 'state_mismatch'
          ? 'セキュリティ検証に失敗しました。もう一度お試しください。'
          : 'Google 登録に失敗しました。時間をおいて再度お試しください。',
      );
    }
  }, [search?.oauth_error]);

  const handleGoogleSignup = () => {
    window.location.href = googleOAuthAuthorizeUrl();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }
    if (password.length < 12) {
      setError('パスワードは12文字以上で入力してください');
      return;
    }

    setLoading(true);
    try {
      await authRegister(email, password);
      trackEvent('register_submitted');
      navigate({ to: '/email-sent', search: { email } });
    } catch (err) {
      setError(formatErrorMessage(err, '登録に失敗しました。もう一度お試しください'));
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
      <div className="w-full max-w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10">
        {/* Title & Subtitle */}
        <div className="mb-8 text-center">
          <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
          <p className="mt-2 text-[14px] text-[#706d65]">無料で始める</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-3 text-sm text-[#991b1b]">
            {error}
          </div>
        )}

        {/* Invite Token Info */}
        {getPendingInviteToken() && (
          <div className="mb-4 rounded-[6px] border-l-4 border-[#3b82f6] bg-[#eff6ff] p-3 text-sm text-[#1e40af]">
            登録後、招待されたメールアドレスでログインすると参加が完了します。
          </div>
        )}

        {/* Google Sign-up */}
        <button
          type="button"
          onClick={handleGoogleSignup}
          disabled={loading}
          className="flex h-[44px] w-full items-center justify-center gap-3 rounded-[6px] border border-[#d6d3d0] bg-white text-[14px] font-medium text-[#23221e] transition hover:bg-[#f8f7f6] disabled:opacity-50"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path
              fill="#FFC107"
              d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            />
            <path
              fill="#FF3D00"
              d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
            />
            <path
              fill="#4CAF50"
              d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
            />
            <path
              fill="#1976D2"
              d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            />
          </svg>
          Google で登録
        </button>

        <div className="my-6 flex items-center gap-3 text-[12px] text-[#a09d95]">
          <div className="h-px flex-1 bg-[#edebe8]" />
          <span>または</span>
          <div className="h-px flex-1 bg-[#edebe8]" />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="mb-2 block text-[13px] font-medium text-[#23221e]">
              メールアドレス
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={loading}
              className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] px-[14px] text-[14px] placeholder-[#c1bdb7] transition-all focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25 disabled:bg-[#f5f5f5]"
            />
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="mb-2 block text-[13px] font-medium text-[#23221e]">
              パスワード
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="12文字以上"
              disabled={loading}
              className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] px-[14px] text-[14px] placeholder-[#c1bdb7] transition-all focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25 disabled:bg-[#f5f5f5]"
            />
          </div>

          {/* Confirm Password Field */}
          <div>
            <label
              htmlFor="confirmPassword"
              className="mb-2 block text-[13px] font-medium text-[#23221e]"
            >
              パスワード（確認）
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              minLength={12}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="もう一度入力"
              disabled={loading}
              className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] px-[14px] text-[14px] placeholder-[#c1bdb7] transition-all focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25 disabled:bg-[#f5f5f5]"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="mt-6 h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-[15px] font-semibold text-white transition-all hover:bg-[#2563eb] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? '登録中...' : '無料で始める'}
          </button>
        </form>

        {/* Trial Info */}
        <p className="mt-6 text-center text-[13px] text-[#706d65]">
          14日間無料トライアル・クレカ不要
        </p>

        {/* Footer Link */}
        <div className="mt-6 border-t border-[#edebe8] pt-4 text-center text-[13px] text-[#706d65]">
          すでにアカウントをお持ちの方はこちら{' '}
          <Link to="/login" className="font-medium text-[#3b82f6] hover:text-[#1d4ed8]">
            ログイン
          </Link>
        </div>
      </div>
    </div>
  );
}
